import { useEffect, useRef, useState } from "react";

interface OtpModalProps {
    /** Returns current attempts left — getter avoids stale closure in parent */
    getAttemptsLeft: () => number;
    /** Returns current locked state — getter avoids stale closure in parent */
    getLocked: () => boolean;
    /** Called with a submit function so the parent/modal confirm button can trigger it */
    onSubmitReady?: (submitFn: () => void) => void;
    onSubmit: (otp: string) => void;
    onResend: () => void;
}

const OTP_EXPIRY = 300; // seconds eq 5 min
const MAX_ATTEMPTS = 3;

const OtpModal = ({ getAttemptsLeft, getLocked, onSubmitReady, onSubmit, onResend }: OtpModalProps) => {

    const [otp, setOtp] = useState(["", "", "", "", "", ""]);
    const [expiresIn, setExpiresIn] = useState(OTP_EXPIRY);

    const [attemptsLeft, setAttemptsLeft] = useState(() => getAttemptsLeft());
    const [locked, setLocked] = useState(() => getLocked());

    const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

    const syncState = () => {
        setAttemptsLeft(getAttemptsLeft());
        setLocked(getLocked());
    };

    // Keep the dynamic state tracking synced if the parent updates outside resend loops
    useEffect(() => {
        syncState();
    }, [getAttemptsLeft, getLocked]);

    useEffect(() => {
        const timer = setInterval(() => {
            setExpiresIn((prev) => {
                if (prev <= 1) {
                    clearInterval(timer);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(timer);
    }, [expiresIn === OTP_EXPIRY]); // Restarts loop when countdown sets back to max threshold

    const isComplete = otp.every((d) => d !== "");

    useEffect(() => {
        if (!onSubmitReady) return;

        if (isComplete && !locked && expiresIn > 0) {
            onSubmitReady(() => {
                onSubmit(otp.join(""));
                syncState();
            });
        } else {
            onSubmitReady(() => {});
        }
    }, [otp, locked, expiresIn]);

    const handleChange = (index: number, value: string) => {
        if (!/^\d*$/.test(value)) return;
        const newOtp = [...otp];

        newOtp[index] = value.slice(-1);
        setOtp(newOtp);

        if (value && index < 5) inputRefs.current[index + 1]?.focus();
    };

    const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
        if (e.key === "Backspace" && !otp[index] && index > 0) inputRefs.current[index - 1]?.focus();
    };

    const handlePaste = (e: React.ClipboardEvent) => {
        e.preventDefault();
        const pasted = e.clipboardData.getData("text/plain").replace(/\D/g, "").slice(0, 6);
        if (!pasted) return;

        const digits = [...pasted.split(""), ...Array(6).fill("")].slice(0, 6);
        setOtp(digits);

        const focusIndex = Math.min(pasted.length, 5);
        inputRefs.current[focusIndex]?.focus();
    };

    const formatTime = (s: number) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

    const isExpired = expiresIn <= 0;
    const canSubmit = isComplete && !locked && !isExpired;

    return (
        <div className="w-full max-w-full mx-auto px-1 py-4 flex flex-col items-center box-border select-none">
            <p className="text-sm font-medium mb-6 text-center w-full">
                Enter the 6-digit code sent to your email
            </p>

            {/* Input Layout Container — Completely Responsive */}
            <div
                className="w-full grid grid-cols-6 gap-2 sm:gap-3 max-w-[340px] mb-6 mx-auto justify-center"
                style={{
                    opacity: isExpired ? 0.4 : 1,
                    pointerEvents: isExpired || locked ? "none" : "auto",
                    transition: "opacity 0.3s",
                }}
            >
                {otp.map((digit, index) => (
                    <input
                        key={index}
                        type="text"
                        inputMode="numeric"
                        maxLength={1}
                        value={digit}
                        onChange={(e) => handleChange(index, e.target.value)}
                        onKeyDown={(e) => handleKeyDown(index, e)}
                        onPaste={handlePaste}
                        ref={(el) => (inputRefs.current[index] = el)}
                        className="w-full aspect-square text-center text-lg sm:text-xl font-bold border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-transparent text-foreground border-border box-border"
                        autoFocus={index === 0}
                        disabled={locked || isExpired}
                    />
                ))}
            </div>

            {/* Status Messages & Utilities */}
            <div className="w-full max-w-[340px] text-center flex flex-col items-center">
                <p className="text-xs text-muted-foreground mb-3">
                    Expires in: <b className="font-semibold text-foreground">{formatTime(expiresIn)}</b>
                </p>

                {attemptsLeft < MAX_ATTEMPTS && !locked && (
                    <p className="text-xs text-red-500 font-medium mb-3">
                        Attempts left: {attemptsLeft}
                    </p>
                )}

                {locked && (
                    <p className="text-xs text-red-600 font-medium mb-3">
                        Too many failed attempts. Please resend OTP.
                    </p>
                )}

                <button
                    type="button"
                    className="w-full rounded-[32px] h-[44px] text-sm font-medium bg-primary text-white disabled:opacity-40 disabled:cursor-not-allowed transition-opacity shadow-sm"
                    disabled={!canSubmit}
                    onClick={() => {
                        onSubmit(otp.join(""));
                        syncState();
                    }}
                >
                    Verify
                </button>

                <button
                    type="button"
                    className="text-xs underline mt-4 font-medium text-muted-foreground hover:text-primary transition-colors disabled:opacity-40 disabled:cursor-not-allowed block mx-auto"
                    disabled={!isExpired && !locked}
                    onClick={async () => {
                        // 1. Force instant local unlock UI state response to make buttons active again immediately
                        setAttemptsLeft(MAX_ATTEMPTS);
                        setLocked(false);
                        setExpiresIn(OTP_EXPIRY);
                        setOtp(["", "", "", "", "", ""]);

                        // 2. Fire parent callback logic to let the backend generate the new code sequence
                        await onResend();

                        // 3. Keep inputs focused
                        setTimeout(() => inputRefs.current[0]?.focus(), 50);
                    }}
                >
                    Resend OTP
                </button>
            </div>
        </div>
    );
};

export default OtpModal;